import { ref, onUnmounted } from 'vue'

// Hover-to-open bookkeeping for the column stats popover, shared by every table
// that offers one (Zeek logs, ip enrichments, generic selector types).

const HOVER_DELAY_MS = 400

/**
 * @param {{enabled?: () => boolean}} [options] `enabled` gates the hover
 *   entirely — tables pass a check on their stats provider prop, so a table
 *   without one behaves as though the feature doesn't exist.
 */
export function useColumnStatsHover({ enabled = () => true } = {}) {
  const show = ref(false)
  const field = ref(null)
  const label = ref(null)
  const anchor = ref(null)
  let hoverTimeout = null

  function clearPending() {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      hoverTimeout = null
    }
  }

  function close() {
    clearPending()
    show.value = false
    field.value = null
    label.value = null
    anchor.value = null
  }

  /**
   * @param {MouseEvent} event
   * @param {?string} fieldName pass null for columns with no stats (a select
   *   checkbox, a caret) so they simply don't open one.
   * @param {?string} labelText display name; falls back to the field name.
   */
  function onHeaderEnter(event, fieldName, labelText = null) {
    if (!enabled() || !fieldName) return
    clearPending()
    const anchorEl = event.target.closest('th')
    hoverTimeout = setTimeout(() => {
      field.value = fieldName
      label.value = labelText
      anchor.value = anchorEl
      show.value = true
    }, HOVER_DELAY_MS)
  }

  function onHeaderLeave(event) {
    clearPending()
    // Keep it open if the cursor moved into the popover itself.
    if (!show.value) return
    const popoverEl = document.querySelector('.column-stats-popover')
    if (!popoverEl || !popoverEl.contains(event.relatedTarget)) close()
  }

  onUnmounted(clearPending)

  return { show, field, label, anchor, onHeaderEnter, onHeaderLeave, close }
}
